import React, { useContext } from 'react';
import styled from 'styled-components';

import Row from 'components/Common/Row';
import FeatureGatesContext from 'contexts/FeatureGatesContext';
import Icon from 'kingpin/atoms/Icon';
import Typography from 'kingpin/atoms/Typography';

import useFormatPaymentAmount from './useFormatPaymentAmount';
import { getPaymentDisplay } from './utils';

const Card = styled.div<{ $isDisqualified: boolean }>`
  margin: 16px 0px;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid
    ${(props) =>
      props.$isDisqualified
        ? 'var(--color-border-secondary)'
        : 'var(--color-success-border)'};
  background-color: ${(props) =>
    props.$isDisqualified
      ? 'var(--color-bg-secondary)'
      : 'var(--color-success-bg)'};
`;

const IconBadge = styled.div<{ $isDisqualified: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${(props) =>
    props.$isDisqualified
      ? 'var(--color-bg-tertiary)'
      : 'var(--color-status-success-transparent)'};
`;

const PaymentLine = styled.div<{ $isDisqualified: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 8px 0px;

  &:not(:last-of-type) {
    border-bottom: 1px solid
      ${(props) =>
        props.$isDisqualified
          ? 'var(--color-border-secondary)'
          : 'var(--color-success-border)'};
  }
`;

const PaymentAmount = styled.span<{ $isDisqualified: boolean }>`
  flex-shrink: 0;
  text-decoration: ${(props) =>
    props.$isDisqualified ? 'line-through' : 'none'};
`;

// The "extra" at the bottom of a driver's reward breakdown: their ad-hoc
// recognition payments, already included in the total. Renders nothing when
// the record has none (missing / null / [] all mean none).
const AdditionalPaymentsRow = ({
  rewardOutput,
}: {
  rewardOutput: BonusTypes.Rewards.AnyDriverRewardsOutput;
}) => {
  const formatAmount = useFormatPaymentAmount();
  const { isAdditionalPaymentsEnabled } = useContext(FeatureGatesContext);
  const payments = rewardOutput.additionalPayments ?? [];
  const total = rewardOutput.totalAdditionalPayments ?? 0;

  // Hidden until the account's additionalPayments gate is opened.
  if (!isAdditionalPaymentsEnabled || payments.length === 0 || total <= 0) {
    return null;
  }

  // A disqualified driver's payments add $0, so the card drops its green
  // emphasis rather than celebrating money the driver won't receive.
  const isDisqualified = rewardOutput.eligibleForBonus === 'disqualified';
  const accentColor = isDisqualified
    ? 'var(--color-text-muted)'
    : 'var(--color-status-success)';

  return (
    <Card
      $isDisqualified={isDisqualified}
      data-testid={'additional-payments-row'}
    >
      <Row spaceBetween centerAlign style={{ gap: 16, marginBottom: 8 }}>
        <Row centerAlign style={{ gap: 12 }}>
          <IconBadge $isDisqualified={isDisqualified}>
            <Icon icon={'sparkles-outline'} color={accentColor} />
          </IconBadge>
          <div>
            <Typography.Header type={'H5'}>
              {'Additional Payments'}
            </Typography.Header>
            <Typography.Body type={'Body 12'} color={'var(--color-text-muted)'}>
              {'Included in total'}
            </Typography.Body>
          </div>
        </Row>
        <Typography.Header type={'H2'} color={accentColor}>
          {isDisqualified
            ? `${formatAmount(0)} (disqualified)`
            : `+${formatAmount(total)}`}
        </Typography.Header>
      </Row>
      {payments.map((payment) => (
        <PaymentLine key={payment.id} $isDisqualified={isDisqualified}>
          <Typography.Body type={'Label'}>
            {getPaymentDisplay(payment).title}
          </Typography.Body>
          <PaymentAmount $isDisqualified={isDisqualified}>
            <Typography.Body type={'Label'} color={accentColor}>
              {`+${formatAmount(payment.amount)}`}
            </Typography.Body>
          </PaymentAmount>
        </PaymentLine>
      ))}
    </Card>
  );
};

export default AdditionalPaymentsRow;
